/**
 * Platform-wide CodeLive quiz level-rules defaults (GET/PUT backend).
 * Applied for every session so pre/post quiz timing & defaults stay consistent.
 */

import { authService } from '../../../utils/authService';
import type { LevelRule, QuizSlot, TopicQuizConfig } from '../types/topicQuiz';
import {
  QUIZ_FIXED_QUESTION_COUNT,
  QUIZ_MAX_DURATION_SECONDS,
  QUIZ_SECONDS_BY_LEVEL,
} from '../types/topicQuiz';

export type CodeliveQuizSlotDefaults = {
  fixedQuestionCount: number;
  maxDurationSeconds: number;
  defaultLevelRules: LevelRule[];
};

export type CodeliveQuizDefaults = {
  secondsByLevel: Record<1 | 2 | 3, number>;
  pre: CodeliveQuizSlotDefaults;
  post: CodeliveQuizSlotDefaults;
};

function toLevelRules(
  rules: Array<{ level?: number; question_count?: number; questionCount?: number }> | undefined,
  fallback: LevelRule[],
): LevelRule[] {
  if (!rules?.length) return fallback.map((r) => ({ ...r }));
  return ([1, 2, 3] as const).map((level) => {
    const found = rules.find((r) => Number(r.level) === level);
    return {
      level,
      questionCount: Number(found?.question_count ?? found?.questionCount ?? 0),
    };
  });
}

function fallbackDefaults(): CodeliveQuizDefaults {
  return {
    secondsByLevel: { ...QUIZ_SECONDS_BY_LEVEL },
    pre: {
      fixedQuestionCount: QUIZ_FIXED_QUESTION_COUNT.pre,
      maxDurationSeconds: QUIZ_MAX_DURATION_SECONDS.pre,
      defaultLevelRules: [
        { level: 1, questionCount: 1 },
        { level: 2, questionCount: 1 },
        { level: 3, questionCount: 0 },
      ],
    },
    post: {
      fixedQuestionCount: QUIZ_FIXED_QUESTION_COUNT.post,
      maxDurationSeconds: QUIZ_MAX_DURATION_SECONDS.post,
      defaultLevelRules: [
        { level: 1, questionCount: 2 },
        { level: 2, questionCount: 2 },
        { level: 3, questionCount: 1 },
      ],
    },
  };
}

export function mapApiQuizDefaults(raw: Record<string, unknown> | null | undefined): CodeliveQuizDefaults {
  const base = fallbackDefaults();
  if (!raw) return base;
  const seconds = (raw.seconds_by_level || raw.secondsByLevel || {}) as Record<string, number>;
  const pre = (raw.pre || {}) as Record<string, unknown>;
  const post = (raw.post || {}) as Record<string, unknown>;
  return {
    secondsByLevel: {
      1: Number(seconds['1'] ?? base.secondsByLevel[1]),
      2: Number(seconds['2'] ?? base.secondsByLevel[2]),
      3: Number(seconds['3'] ?? base.secondsByLevel[3]),
    },
    pre: {
      fixedQuestionCount: Number(
        pre.fixed_question_count ?? pre.fixedQuestionCount ?? base.pre.fixedQuestionCount,
      ),
      maxDurationSeconds: Number(
        pre.max_duration_seconds ?? pre.maxDurationSeconds ?? base.pre.maxDurationSeconds,
      ),
      defaultLevelRules: toLevelRules(
        (pre.default_level_rules ?? pre.defaultLevelRules) as Array<{
          level?: number;
          question_count?: number;
          questionCount?: number;
        }>,
        base.pre.defaultLevelRules,
      ),
    },
    post: {
      fixedQuestionCount: Number(
        post.fixed_question_count ?? post.fixedQuestionCount ?? base.post.fixedQuestionCount,
      ),
      maxDurationSeconds: Number(
        post.max_duration_seconds ?? post.maxDurationSeconds ?? base.post.maxDurationSeconds,
      ),
      defaultLevelRules: toLevelRules(
        (post.default_level_rules ?? post.defaultLevelRules) as Array<{
          level?: number;
          question_count?: number;
          questionCount?: number;
        }>,
        base.post.defaultLevelRules,
      ),
    },
  };
}

export async function fetchCodeliveQuizDefaults(): Promise<CodeliveQuizDefaults> {
  try {
    const response = await authService.get('/session/codelive/quiz-defaults/');
    if (response?.data?.success && response.data.data) {
      return mapApiQuizDefaults(response.data.data);
    }
  } catch (err) {
    console.error('Failed to load CodeLive quiz defaults', err);
  }
  return fallbackDefaults();
}

export async function saveCodeliveQuizDefaults(
  defaults: CodeliveQuizDefaults,
): Promise<CodeliveQuizDefaults> {
  const payload = {
    seconds_by_level: {
      '1': defaults.secondsByLevel[1],
      '2': defaults.secondsByLevel[2],
      '3': defaults.secondsByLevel[3],
    },
    pre: {
      fixed_question_count: defaults.pre.fixedQuestionCount,
      max_duration_seconds: defaults.pre.maxDurationSeconds,
      default_level_rules: defaults.pre.defaultLevelRules.map((r) => ({
        level: r.level,
        question_count: r.questionCount,
      })),
    },
    post: {
      fixed_question_count: defaults.post.fixedQuestionCount,
      max_duration_seconds: defaults.post.maxDurationSeconds,
      default_level_rules: defaults.post.defaultLevelRules.map((r) => ({
        level: r.level,
        question_count: r.questionCount,
      })),
    },
  };

  const response = await authService.put('/session/codelive/quiz-defaults/', payload);
  if (response?.data?.success && response.data.data) {
    return mapApiQuizDefaults(response.data.data);
  }
  return defaults;
}

export function defaultsForSlot(
  defaults: CodeliveQuizDefaults,
  slot: QuizSlot,
): CodeliveQuizSlotDefaults {
  return slot === 'pre' ? defaults.pre : defaults.post;
}

/** Build the single shared quiz config for a slot (same for every topic/session). */
export function quizConfigFromGlobalDefaults(
  slot: QuizSlot,
  defaults: CodeliveQuizDefaults | null | undefined,
): TopicQuizConfig {
  if (!defaults) {
    return {
      slot,
      mode: 'level-rules',
      levelRules: (slot === 'pre'
        ? fallbackDefaults().pre
        : fallbackDefaults().post
      ).defaultLevelRules.map((r) => ({ ...r })),
    };
  }
  const slotDefaults = defaultsForSlot(defaults, slot);
  return {
    slot,
    mode: 'level-rules',
    levelRules: slotDefaults.defaultLevelRules.map((r) => ({ ...r })),
  };
}

/** Topics only keep pre/post on/off; rules always come from global defaults. */
export function withGlobalQuizRules(
  topics: Array<{
    id: string;
    index: number;
    title: string;
    durationMinutes: number;
    description?: string;
    preQuiz?: unknown;
    postQuiz?: unknown;
  }>,
  defaults: CodeliveQuizDefaults | null | undefined,
) {
  return topics.map((topic) => ({
    ...topic,
    preQuiz: topic.preQuiz ? quizConfigFromGlobalDefaults('pre', defaults) : undefined,
    postQuiz: topic.postQuiz
      ? quizConfigFromGlobalDefaults('post', defaults)
      : undefined,
  }));
}

/**
 * Canonical slim topic_plan for create/upsert.
 * Always emits explicit boolean pre_quiz/post_quiz on every topic and subtopic.
 *
 * Format:
 * - Topic with subtopics: topic pre/post = false; each sub has pre_quiz + post_quiz
 *   (post_quiz defaults true — required; pre_quiz only when trainer enabled it)
 * - Topic without subtopics: post_quiz defaults true; pre_quiz only when enabled
 */
export function toBackendTopicPlan(
  topics: Array<{
    id: string;
    index: number;
    title: string;
    durationMinutes: number;
    description?: string;
    preQuiz?: unknown;
    postQuiz?: unknown;
    workshopDayIndex?: number;
    subtopics?: Array<{
      id: string;
      index: number;
      title: string;
      durationMinutes: number;
      preQuiz?: unknown;
      postQuiz?: unknown;
    }>;
  }>,
) {
  return topics.map((topic) => {
    const hasSubs = Boolean(topic.subtopics?.length);
    const base = {
      id: topic.id,
      index: topic.index,
      title: topic.title,
      duration_minutes: topic.durationMinutes,
      description: topic.description || '',
      // Quizzes live on subtopics when present — never leave topic flags ambiguous.
      pre_quiz: hasSubs ? false : Boolean(topic.preQuiz),
      post_quiz: hasSubs
        ? false
        : topic.postQuiz === undefined || topic.postQuiz === null
          ? true
          : Boolean(topic.postQuiz),
      ...(typeof topic.workshopDayIndex === 'number'
        ? { workshop_day_index: topic.workshopDayIndex }
        : {}),
    };

    if (!hasSubs || !topic.subtopics) {
      return base;
    }

    return {
      ...base,
      subtopics: topic.subtopics.map((s) => ({
        id: s.id,
        index: s.index,
        title: s.title,
        duration_minutes: s.durationMinutes,
        pre_quiz: Boolean(s.preQuiz),
        post_quiz:
          s.postQuiz === undefined || s.postQuiz === null
            ? true
            : Boolean(s.postQuiz),
      })),
    };
  });
}
